import React from 'react';
import { FeedbackStatusProps } from '@/types/components';
import { StatusVariant } from '@/types/common';

// Predefined status mappings for common statuses
const defaultStatusMappings: Record<string, StatusVariant> = {
  // Success states
  complete: 'success',
  completed: 'success',
  active: 'success',
  approved: 'success',
  delivered: 'success',
  online: 'success',

  // Warning states
  'in delivery': 'warning',
  'in progress': 'warning',
  'pending pickup': 'warning',
  'picking up': 'warning',
  processing: 'warning',

  // Error states
  failed: 'error',
  error: 'error',
  rejected: 'error',
  offline: 'error',
  unavailable: 'error',

  // Info states
  allocating: 'info',
  queueing: 'info',
  scheduled: 'info',
  reviewing: 'info',

  // Neutral states
  canceled: 'neutral',
  cancelled: 'neutral',
  draft: 'neutral',
  idle: 'neutral',

  // Pending states
  pending: 'pending',
  waiting: 'pending',
  hold: 'pending',
};

export const FeedbackStatus: React.FC<FeedbackStatusProps> = ({
  status,
  variant,
  size = 'medium',
  customColors,
  className = '',
}) => {
  const resolvedVariant =
    variant || defaultStatusMappings[status.toLowerCase()] || 'neutral';

  const baseClasses = 'inline-flex items-center font-medium rounded-full';

  const sizeClasses = {
    small: 'px-2 py-0.5 text-xs',
    medium: 'px-2.5 py-0.5 text-xs',
    large: 'px-3 py-1 text-sm',
  };

  // DS state tokens — no raw palette classes
  const variantClasses = {
    success: 'bg-success-lighter text-success-dark',
    warning: 'bg-away-lighter text-away-dark',      // yellow
    error: 'bg-error-lighter text-error-dark',
    info: 'bg-information-lighter text-information-dark',
    neutral: 'bg-bg-weak-50 text-text-sub-600',
    pending: 'bg-warning-lighter text-warning-dark', // orange
  };

  let finalClasses = `${baseClasses} ${sizeClasses[size]}`;

  if (customColors) {
    finalClasses += ` ${className}`;
  } else {
    finalClasses += ` ${variantClasses[resolvedVariant]} ${className}`;
  }

  const style = customColors
    ? {
        backgroundColor: customColors.backgroundColor,
        color: customColors.textColor,
      }
    : undefined;

  return (
    <span className={finalClasses.trim()} style={style}>
      {status}
    </span>
  );
};

export default FeedbackStatus;
